import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';
import type { CategoryEntity } from '@actual-app/core/types/models';

import { useCreateCategoryMutation } from '#budget';
import {
  filaConEmoji,
  HojaSeleccion,
  PastillaSaldo,
} from '#components/mobile/ui/HojaSeleccion';
import type {
  FilaSeleccion,
  SeccionSeleccion,
} from '#components/mobile/ui/HojaSeleccion';
import { conSimboloEuro } from '#components/mobile/ui/Importe';
import { color } from '#components/mobile/ui/tokens';
import { useCategories } from '#hooks/useCategories';
import { useFormat } from '#hooks/useFormat';
import { SheetNameProvider } from '#hooks/useSheetName';
import { useSheetValue } from '#hooks/useSheetValue';
import type { Modal as ModalType } from '#modals/modalsSlice';
import { aqlQuery } from '#queries/aqlQuery';
import { envelopeBudget } from '#spreadsheet/bindings';

import {
  bloquesCategoria,
  categoriaIngreso,
  gruposVisibles,
  guardarReciente,
  leerRecientes,
} from './seleccionCategoria';
import type { GrupoVisible } from './seleccionCategoria';

type OpcionesModal = Extract<
  ModalType,
  { name: 'category-autocomplete' }
>['options'];

type HojaCategoriaProps = OpcionesModal & {
  /** Quita la hoja de la pila de modales (y avisa a `onClose`). */
  onCerrar: () => void;
};

/** Fila de «Listo para asignar»: su saldo es el del presupuesto entero. */
const ID_LISTO = 'listo-para-asignar';
/** Categoría ficticia «Listo para asignar» de Traspasar/Cubrir en Actual. */
const ID_TO_BUDGET = 'to-budget';

/**
 * Selector de categoría móvil (H76): sustituye en móvil al modal
 * `category-autocomplete` de Actual con la hoja de selección de Rumbo.
 */
export function HojaCategoria({
  title,
  categoryGroups,
  onSelect,
  month,
  showNoneOption,
  closeOnSelect,
  contexto,
  permitirCrear,
  listoParaAsignar,
  onCerrar,
}: HojaCategoriaProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const { data: { grouped, list } = { grouped: [], list: [] } } =
    useCategories();
  const createCategory = useCreateCategoryMutation();
  const [recientes] = useState(leerRecientes);
  // Nombre de la categoría nueva mientras se elige su grupo.
  const [nueva, setNueva] = useState<string | null>(null);
  const sugeridaId = useUltimaCategoria(
    contexto?.beneficiarioId,
    contexto?.movimientoId,
  );

  const grupos = useMemo(
    () => gruposVisibles(categoryGroups ?? grouped),
    [categoryGroups, grouped],
  );
  const ingreso = useMemo(
    () =>
      listoParaAsignar && (contexto?.importe ?? 0) > 0
        ? categoriaIngreso(list)
        : null,
    [listoParaAsignar, contexto?.importe, list],
  );

  const secciones = useMemo(
    () =>
      seccionesDe(
        // Sin `onSelect` (la lista de categorías en rojo del Plan) la hoja
        // solo enseña: sin atajos.
        typeof onSelect === 'function'
          ? bloquesCategoria({ grupos, sugeridaId, recientes, ingreso })
          : bloquesCategoria({ grupos, recientes: [] }),
        {
          listo: t('Ready to Assign'),
          sugerida: t('Suggested'),
          recientes: t('Recent'),
          ultimaVez: contexto?.beneficiario
            ? t('Last time with {{payee}}', { payee: contexto.beneficiario })
            : t('Last used with this payee'),
        },
      ),
    [
      grupos,
      sugeridaId,
      recientes,
      ingreso,
      onSelect,
      contexto?.beneficiario,
      t,
    ],
  );

  const elegir = (id: string | null, nombre: string) => {
    if (onSelect) {
      if (id && id !== ID_TO_BUDGET) {
        guardarReciente(id);
      }
      onSelect(id, nombre);
    }
    if (closeOnSelect !== false) {
      onCerrar();
    }
  };

  const nombreDe = (id: string) =>
    list.find(c => c.id === id)?.name ??
    grupos.flatMap(g => g.categorias).find(c => c.id === id)?.name ??
    '';

  const pastilla = month
    ? (fila: FilaSeleccion) => (
        <SaldoCategoria id={fila.id === ID_LISTO ? ID_TO_BUDGET : fila.id} />
      )
    : undefined;

  const linea = lineaContexto(contexto, format, t);

  if (nueva != null) {
    return (
      <HojaGrupoNueva
        key="grupo"
        nombre={nueva}
        grupos={gruposVisibles(grouped)}
        onCerrar={onCerrar}
        onElegir={async grupo => {
          try {
            const id = await createCategory.mutateAsync({
              name: nueva,
              groupId: grupo.id,
              isIncome: !!grupo.is_income,
              isHidden: false,
            });
            elegir(id, nueva);
          } catch {
            // La mutación ya avisa del error; se vuelve a la lista.
            setNueva(null);
          }
        }}
      />
    );
  }

  return (
    <SheetNameProvider name={month ? monthUtils.sheetForMonth(month) : ''}>
      <HojaSeleccion
        data-testid="hoja-categoria"
        titulo={title || t('Category')}
        contexto={linea}
        textoBuscar={t('Search category')}
        secciones={secciones}
        pastilla={pastilla}
        onCerrar={onCerrar}
        onElegir={fila =>
          fila.id === ID_LISTO && ingreso
            ? elegir(ingreso.id, ingreso.name)
            : elegir(fila.id, nombreDe(fila.id))
        }
        filaFinal={
          showNoneOption
            ? {
                nombre: t('Uncategorized'),
                detalle: t('Decide later'),
                onElegir: () => elegir(null, ''),
              }
            : undefined
        }
        crear={
          permitirCrear && typeof onSelect === 'function'
            ? {
                etiqueta: nombre =>
                  t('Create category "{{name}}"', { name: nombre }),
                onCrear: setNueva,
              }
            : undefined
        }
      />
    </SheetNameProvider>
  );
}

/** Convierte el orden de la hoja en secciones con sus filas. */
function seccionesDe(
  bloques: ReturnType<typeof bloquesCategoria>,
  textos: {
    listo: string;
    sugerida: string;
    recientes: string;
    ultimaVez: string;
  },
): SeccionSeleccion[] {
  const fila = (c: CategoryEntity, grupo?: string) =>
    filaConEmoji(c.id, c.name, { buscarEn: grupo, detalleBusqueda: grupo });
  return bloques.map(b => {
    switch (b.tipo) {
      case 'listo':
        return {
          id: 'listo',
          titulo: textos.listo,
          sinCabecera: true,
          atajo: true,
          filas: [{ id: ID_LISTO, nombre: textos.listo, emoji: '💶' }],
        };
      case 'sugerida':
        return {
          id: 'sugerida',
          titulo: textos.sugerida,
          icono: 'compass',
          atajo: true,
          filas: [
            {
              ...fila(b.categoria),
              detalle: textos.ultimaVez,
              destacada: true,
            },
          ],
        };
      case 'recientes':
        return {
          id: 'recientes',
          titulo: textos.recientes,
          icono: 'clock',
          atajo: true,
          filas: b.categorias.map(c => fila(c)),
        };
      default:
        return {
          id: `grupo-${b.grupo.id}`,
          titulo: b.grupo.name,
          contador: b.categorias.length,
          filas: b.categorias.map(c => fila(c, b.grupo.name)),
        };
    }
  });
}

/** «Mercadona · −42,18 € · hoy» (lo que se sepa). */
function lineaContexto(
  contexto: HojaCategoriaProps['contexto'],
  format: ReturnType<typeof useFormat>,
  t: ReturnType<typeof useTranslation>['t'],
) {
  if (!contexto) {
    return undefined;
  }
  const partes: ReactNode[] = [];
  if (contexto.beneficiario) {
    partes.push(contexto.beneficiario);
  }
  if (contexto.importe != null && contexto.importe !== 0) {
    const cifra = format(contexto.importe, 'financial');
    partes.push(
      <span
        key="importe"
        style={{
          fontWeight: 800,
          color: contexto.importe < 0 ? color.bad : undefined,
        }}
      >
        {conSimboloEuro(cifra)}
      </span>,
    );
  }
  if (contexto.fecha) {
    partes.push(fechaRelativa(contexto.fecha, t));
  }
  if (partes.length === 0) {
    return undefined;
  }
  return partes.flatMap((p, i) => (i === 0 ? [p] : [' · ', p]));
}

function fechaRelativa(
  fecha: string,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  const hoy = monthUtils.currentDay();
  if (fecha === hoy) {
    return t('today');
  }
  if (fecha === monthUtils.subDays(hoy, 1)) {
    return t('yesterday');
  }
  const [y, m, d] = fecha.split('-').map(Number);
  if (!y || !m || !d) {
    return fecha;
  }
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
  }).format(new Date(y, m - 1, d));
}

/**
 * Última categoría usada con el beneficiario (sin contar el propio
 * movimiento). Una consulta barata: un solo movimiento, el más reciente.
 */
function useUltimaCategoria(
  beneficiarioId: string | null | undefined,
  movimientoId: string | undefined,
): string | null {
  const [id, setId] = useState<string | null>(null);
  useEffect(() => {
    if (!beneficiarioId) {
      setId(null);
      return;
    }
    let vivo = true;
    let consulta = q('transactions')
      .filter({ payee: beneficiarioId, category: { $ne: null } })
      .orderBy({ date: 'desc' })
      .select(['category'])
      .limit(1);
    if (movimientoId) {
      consulta = consulta.filter({ id: { $ne: movimientoId } });
    }
    aqlQuery(consulta)
      .then(({ data }) => {
        if (vivo) {
          const [fila] = data as Array<{ category: string | null }>;
          setId(fila?.category ?? null);
        }
      })
      .catch(() => {
        // Sin sugerida: la hoja sigue funcionando.
      });
    return () => {
      vivo = false;
    };
  }, [beneficiarioId, movimientoId]);
  return id;
}

function SaldoCategoria({ id }: { id: string }) {
  return id === ID_TO_BUDGET ? <SaldoListo /> : <SaldoSobre id={id} />;
}

function SaldoListo() {
  const valor = useSheetValue<'envelope-budget', 'to-budget'>(
    envelopeBudget.toBudget,
  );
  return <PastillaSaldo valor={valor} />;
}

function SaldoSobre({ id }: { id: string }) {
  const valor = useSheetValue<'envelope-budget', 'leftover'>(
    envelopeBudget.catBalance(id),
  );
  return <PastillaSaldo valor={valor} />;
}

/** Segundo paso de «Crear categoría»: en qué grupo va. */
function HojaGrupoNueva({
  nombre,
  grupos,
  onCerrar,
  onElegir,
}: {
  nombre: string;
  grupos: GrupoVisible[];
  onCerrar: () => void;
  onElegir: (grupo: GrupoVisible['grupo']) => void;
}) {
  const { t } = useTranslation();
  const porId = new Map(grupos.map(g => [g.grupo.id, g.grupo]));
  return (
    <HojaSeleccion
      data-testid="hoja-categoria-grupo"
      titulo={t('Create category "{{name}}"', { name: nombre })}
      contexto={t('Choose its group')}
      textoBuscar={t('Search group')}
      secciones={[
        {
          id: 'grupos',
          titulo: t('Groups'),
          filas: grupos.map(g =>
            filaConEmoji(g.grupo.id, g.grupo.name, {
              detalle: t('{{count}} categories', {
                count: g.categorias.length,
              }),
            }),
          ),
        },
      ]}
      onCerrar={onCerrar}
      onElegir={fila => {
        const grupo = porId.get(fila.id);
        if (grupo) {
          onElegir(grupo);
        }
      }}
    />
  );
}
